import React, {useCallback, useState} from "react"
import {useDispatch} from "react-redux"
import { makeStyles } from 'tss-react/mui'
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from '@mui/material'
import Grid from '@mui/material/Grid2';
import {useTranslation} from "react-i18next";
import {Theme} from "@mui/material/styles";
import {useSelector} from "react-redux";
import {RootState} from "../../../Redux";
import api from '../../../Services/api'
import uiRedux from "../../../Redux/uiRedux";
import {ThemeType} from '../../../interfaces'
import {useAppThemeOptions} from '../../../Utils/hooks'

const { getCustomerInfo } = api.create()

const useStyles = makeStyles<ThemeType>()((theme: Theme) => ({
    dialog: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            borderRadius: 0,
        },
    },
    dialogContent: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
}))

const CustomerTokenButton = ({sx}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const themeOptions = useAppThemeOptions()
    const { classes } = useStyles(themeOptions)

    const chainId = useSelector((state: RootState) => state.ui?.settings?.chain?.id)
    const stationId = useSelector((state: RootState) => state.ui?.settings?.station?.id)

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [customerToken, setCustomerToken] = useState<string|null>(null)
    const customerInfo = useSelector((state: RootState) => state.ui.customer)

    const handleOpen = () => {
        setOpen(true);
        setProcessing(false);
    }
    const handleClose = () => setOpen(false);

    const handleExit = useCallback(() => {
        setMessage(null)
        setProcessing(true)
        dispatch(uiRedux.setCustomerInfo({}))

        getCustomerInfo(chainId, stationId, customerToken).then((response) => {
            if (response.ok) {
                dispatch(uiRedux.setCustomerInfo(response.data ?? {}))
                setOpen(false)
            } else {
                setMessage(t('selfService.customerTokenNotFound'))
            }
        }).catch(() => {
            setMessage(t('selfService.customerTokenNotFound'))
        }).finally(() => {
            setProcessing(false)
        })
    }, [chainId, stationId, customerToken, setMessage, setOpen, setProcessing, t, dispatch])

    return (<div>
        <Button
            onClick={handleOpen}
            className="textButtonCompact"
        >
            {t('selfService.companyCustomer')}{customerInfo?.name ? (': ' + customerInfo?.name) : '?'}
        </Button>
        <Dialog
            PaperProps={{
                classes: { root: classes.dialog },
            }}
            classes={{ root: classes.dialog }}
            open={open}
            onClose={handleClose}
            sx={sx}
        >
            <DialogTitle id="campaign-code-dialog-title" className={classes.dialogTitle}>
                {t('selfService.enterCustomerToken')}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid container wrap="nowrap" direction="column" alignItems="center" justifyContent="center" style={{marginTop: 8, opacity: processing ? 0.5 : undefined}}>
                    <TextField
                        name="token"
                        fullWidth
                        placeholder={t('selfService.customerToken')}
                        value={customerToken ?? ''}
                        onChange={(e) => {
                            setMessage(null)
                            setCustomerToken(e.target.value)
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleExit()
                            }
                        }}
                    />
                    <Typography variant="body1" color="error" style={{height: '1em'}}>
                        {message}
                    </Typography>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={4} justifyContent="space-between" width="100%">
                    <Button
                        disabled={processing}
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleClose}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        disabled={processing}
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={handleExit}
                    >
                        {t('common.ok')}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    </div>)
}

export default CustomerTokenButton
